import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { buildTranslator, isRTL } from "@/lib/i18n";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const I18nCtx = createContext(null);
const LANG_KEY = "aviary_lang";

export function I18nProvider({ children }) {
  const { user } = useAuth();
  const [lang, setLang] = useState(() => localStorage.getItem(LANG_KEY) || "en");

  // Sync language from authenticated user profile (only when first known)
  useEffect(() => {
    if (user && user.language && user.language !== lang) {
      setLang(user.language);
      localStorage.setItem(LANG_KEY, user.language);
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply <html dir lang>
  useEffect(() => {
    const root = document.documentElement;
    root.lang = lang;
    root.dir = isRTL(lang) ? "rtl" : "ltr";
  }, [lang]);

  const changeLanguage = useCallback(async (next) => {
    setLang(next);
    localStorage.setItem(LANG_KEY, next);
    if (user) {
      try {
        await api.patch("/auth/me", { language: next });
      } catch {/* server save is best-effort; UI already updated from localStorage */}
    }
  }, [user]);

  const t = useMemo(() => buildTranslator(lang), [lang]);

  return (
    <I18nCtx.Provider value={{ lang, changeLanguage, t, dir: isRTL(lang) ? "rtl" : "ltr" }}>
      {children}
    </I18nCtx.Provider>
  );
}

export const useI18n = () => useContext(I18nCtx);
